// Suppliers — the distributors orders are placed with. Add, edit, deactivate,
// reactivate; remove outright only when no order has ever named the supplier.
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import DataTable from '../../components/DataTable';

const CAT_OPTS = ['incontinence', 'ostomy', 'catheter'];

function ModalBackdrop({ onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: 'var(--card-bg)', borderRadius: 14, width: '100%', maxWidth: 560, boxShadow: '0 24px 48px rgba(0,0,0,0.25)', flexShrink: 0 }}>
        {children}
      </div>
    </div>
  );
}

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [saving, setSaving]       = useState(false);
  const [saveError, setSaveError] = useState('');

  const blankForm = { name: '', contact_name: '', phone: '', email: '', address: '', account_number: '', product_categories: [], notes: '', is_active: true };
  const [form, setForm] = useState(blankForm);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('dme_suppliers').select('*').order('name');
    setSuppliers(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(blankForm); setSaveError(''); setShowModal(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...s }); setSaveError(''); setShowModal(true); };

  const toggleCat = (cat) => {
    setForm(f => ({
      ...f,
      product_categories: f.product_categories.includes(cat)
        ? f.product_categories.filter(c => c !== cat)
        : [...f.product_categories, cat],
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setSaveError('Supplier name is required.'); return; }
    setSaving(true); setSaveError('');
    const payload = { ...form, updated_at: new Date().toISOString() };
    const { error } = editing
      ? await supabase.from('dme_suppliers').update(payload).eq('id', editing.id)
      : await supabase.from('dme_suppliers').insert(payload);
    if (error) { setSaveError(error.message); setSaving(false); return; }
    setShowModal(false);
    load();
    setSaving(false);
  };

  const handleDeactivate = async (s) => {
    if (!window.confirm(`Mark ${s.name} as inactive? It will no longer be offered on new orders.`)) return;
    await supabase.from('dme_suppliers').update({ is_active: false, updated_at: new Date().toISOString() }).eq('id', s.id);
    load();
  };

  const handleReactivate = async (s) => {
    await supabase.from('dme_suppliers').update({ is_active: true, updated_at: new Date().toISOString() }).eq('id', s.id);
    load();
  };

  // Orders keep a supplier_id for their history, so a supplier with orders is
  // deactivated rather than deleted — removing it would orphan those records.
  const handleRemove = async (s) => {
    const { count } = await supabase.from('dme_orders').select('id', { count: 'exact', head: true }).eq('supplier_id', s.id);
    if (count > 0) {
      window.alert(`${s.name} is on ${count} order${count === 1 ? '' : 's'} and can't be removed. Deactivate it instead.`);
      return;
    }
    if (!window.confirm(`Remove ${s.name} permanently? No orders reference it.`)) return;
    await supabase.from('dme_suppliers').delete().eq('id', s.id);
    load();
  };

  const inputStyle = { width: '100%', padding: '8px 10px', fontSize: 12, borderRadius: 7, border: '0.5px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontFamily: 'inherit', boxSizing: 'border-box' };
  const labelStyle = { fontSize: 11, fontWeight: 500, color: 'var(--text2)', marginBottom: 4, display: 'block' };
  const FG = ({ label, children, half }) => (
    <div style={{ flex: half ? '1 1 calc(50% - 6px)' : '1 1 100%', minWidth: 0 }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );

  // Product category is a parallel identifier, not a status — neutral for all.
  const catColor = () => 'var(--text3)';
  const catBg    = () => 'var(--bg2)';

  const columns = [
    {
      accessorKey: 'name', header: 'Supplier', size: 180,
      meta: { mobile: 'primary' },
      cell: info => info.getValue(),
    },
    {
      accessorKey: 'contact_name', header: 'Contact', size: 140,
      meta: { mobile: 'secondary' },
      cell: info => <span style={{ color: 'var(--text2)' }}>{info.getValue() || '—'}</span>,
    },
    {
      accessorKey: 'phone', header: 'Phone', size: 120,
      meta: { mobile: 'secondary' },
      cell: info => <span style={{ color: 'var(--text3)' }}>{info.getValue() || '—'}</span>,
    },
    {
      accessorKey: 'email', header: 'Email', size: 190,
      meta: { mobile: 'hidden', truncate: true },
      cell: info => <span style={{ color: 'var(--text3)' }}>{info.getValue() || '—'}</span>,
    },
    {
      id: 'products', header: 'Products', size: 160, enableSorting: false,
      meta: { mobile: 'secondary' },
      cell: info => {
        const cats = info.row.original.product_categories || [];
        return (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {cats.map(c => (
              <span key={c} style={{ fontSize: 9, fontWeight: 500, padding: '2px 6px', borderRadius: 20, background: catBg(c), color: catColor(c), textTransform: 'capitalize' }}>{c}</span>
            ))}
            {!cats.length && <span style={{ color: 'var(--text3)', fontSize: 11 }}>—</span>}
          </div>
        );
      },
    },
    {
      accessorKey: 'account_number', header: 'Account #', size: 130,
      meta: { mono: true, mobile: 'hidden' },
      cell: info => info.getValue() || '—',
    },
    {
      accessorKey: 'is_active', header: 'Status', size: 100,
      meta: { mobile: 'primary' },
      cell: info => (
        <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 20, background: info.getValue() ? 'var(--success-bg)' : 'var(--bg2)', color: info.getValue() ? 'var(--success)' : 'var(--text3)' }}>
          {info.getValue() ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      id: 'actions', header: '', size: 150, enableSorting: false,
      meta: { mobile: 'secondary' },
      cell: info => {
        const s = info.row.original;
        return (
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-outline btn-sm" style={{ fontSize: 10 }} onClick={() => openEdit(s)}>Edit</button>
            {s.is_active
              ? <button className="btn btn-outline btn-sm" style={{ fontSize: 10 }} onClick={() => handleDeactivate(s)}>Deactivate</button>
              : <button className="btn btn-outline btn-sm" style={{ fontSize: 10 }} onClick={() => handleReactivate(s)}>Reactivate</button>}
            <button className="btn btn-outline btn-sm" style={{ fontSize: 10, color: 'var(--danger)' }} onClick={() => handleRemove(s)}>Remove</button>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>Suppliers</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>Drop-ship partners for all product lines</div>
        </div>
        <button className="btn btn-primary" onClick={openNew}>+ Add Supplier</button>
      </div>

      <div className="card">
        {!loading && suppliers.length === 0 ? (
          <div className="empty-state" style={{ padding: '60px 20px' }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>🏭</div>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}>No suppliers yet</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>Add your first drop-ship supplier (e.g. McKesson, Medline) to get started.</div>
            <button className="btn btn-primary btn-sm" onClick={openNew}>+ Add Supplier</button>
          </div>
        ) : (
          <DataTable
            tableId="dme-suppliers"
            columns={columns}
            data={suppliers}
            getRowId={s => s.id}
            loading={loading}
            emptyMessage="No suppliers yet."
            maxHeight="calc(100vh - 260px)"
          />
        )}
      </div>

      {showModal && (
        <ModalBackdrop onClose={() => setShowModal(false)}>
          <div style={{ padding: '18px 24px', borderBottom: '0.5px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{editing ? 'Edit Supplier' : 'Add Supplier'}</div>
            <button className="btn btn-outline btn-sm" onClick={() => setShowModal(false)}>Cancel</button>
          </div>
          <div style={{ padding: '18px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              <FG label="Supplier name *">
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} placeholder="e.g. McKesson Medical-Surgical" />
              </FG>
              <FG label="Contact name" half>
                <input value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} style={inputStyle} />
              </FG>
              <FG label="Account number" half>
                <input value={form.account_number} onChange={e => setForm(f => ({ ...f, account_number: e.target.value }))} style={inputStyle} />
              </FG>
              <FG label="Phone" half>
                <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} style={inputStyle} />
              </FG>
              <FG label="Email" half>
                <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} style={inputStyle} />
              </FG>
              <FG label="Address">
                <input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} style={inputStyle} />
              </FG>
            </div>

            <div>
              <label style={labelStyle}>Product categories supplied</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {CAT_OPTS.map(cat => {
                  const active = form.product_categories.includes(cat);
                  return (
                    <button key={cat} type="button" onClick={() => toggleCat(cat)} style={{
                      padding: '5px 14px', borderRadius: 20, fontSize: 11, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
                      background: active ? catBg(cat) : 'var(--bg2)',
                      color: active ? catColor(cat) : 'var(--text3)',
                      border: `1.5px solid ${active ? catColor(cat) : 'var(--border)'}`,
                      textTransform: 'capitalize',
                    }}>
                      {active ? '✓ ' : ''}{cat}
                    </button>
                  );
                })}
              </div>
            </div>

            <FG label="Notes">
              <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                style={{ ...inputStyle, minHeight: 56, resize: 'vertical', lineHeight: 1.5 }} />
            </FG>

            {saveError && <div style={{ fontSize: 12, color: 'var(--danger)', background: 'var(--danger-bg)', padding: '8px 12px', borderRadius: 7 }}>{saveError}</div>}
          </div>
          <div style={{ padding: '14px 24px', borderTop: '0.5px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
            <button className="btn btn-primary" disabled={saving} onClick={handleSave}>{saving ? 'Saving…' : editing ? 'Save Changes' : 'Add Supplier'}</button>
          </div>
        </ModalBackdrop>
      )}
    </div>
  );
}
